// vendorsV2Projection.js — Pure utility functions for BusinessPartnersPage V2.
// No React, no Firestore. Consumed via useMemo in BusinessPartnersPage.
// Analog: src/utils/contactProjection.js

// ---------------------------------------------------------------------------
// VENDOR_SORT_OPTIONS — three sort keys with labels and descriptions.
//
// 'recent'  — Stable proxy for insertion order; vendors have no createdAt
//             field (D-05/D-07). Comparator returns 0 (preserve existing order).
// 'name'    — Alphabetical A→Z by company name (or "First Last" fallback).
// 'deals'   — Descending all-time deal count derived via buildVendorDealCounts.
// ---------------------------------------------------------------------------

export const VENDOR_SORT_OPTIONS = [
    { value: 'recent', label: 'Recently added', desc: 'Newest records first' },
    { value: 'name',   label: 'Name A–Z',        desc: 'Alphabetical by company name' },
    { value: 'deals',  label: 'Most deals',       desc: 'Partners with the most all-time deals' },
];

// ---------------------------------------------------------------------------
// displayName — private helper for consistent name resolution.
//
// Prefers company; falls back to trimmed "First Last". Returns empty string
// when neither field is populated (callers render a placeholder).
// ---------------------------------------------------------------------------

function displayName(vendor) {
    const company = (vendor.company || '').trim();
    if (company) return company;
    return `${vendor.firstName || ''} ${vendor.lastName || ''}`.trim();
}

// ---------------------------------------------------------------------------
// applyVendorSearch — company-name-only substring filter (D-06).
//
// Matches vendors whose `company` field contains the query string, case-
// insensitively. No other fields are searched (firstName/lastName/email/phone
// are intentionally excluded per D-06 and the UI-SPEC).
//
// Returns the input array reference unchanged for empty/whitespace queries —
// avoids allocating a new array and is safe to pass to a strict-equality check.
// ---------------------------------------------------------------------------

export function applyVendorSearch(items, query) {
    if (!query || !query.trim()) return items;
    const q = query.trim().toLowerCase();
    return items.filter(v => (v.company || '').toLowerCase().includes(q));
}

// ---------------------------------------------------------------------------
// makeVendorSortComparator — comparator factory for Array.prototype.sort.
//
// sortKey: 'recent' | 'name' | 'deals'
// dealCounts: Map<vendorId, number> — required for 'deals'; pass new Map() for
//             other keys to avoid a null-check burden on callers.
//
// 'deals'  — Descending by count; vendors absent from the map rank as 0.
// 'name'   — Ascending localeCompare on lowercased display name (company or
//             "First Last" fallback). Case-insensitive via toLowerCase before
//             the comparison rather than localeCompare sensitivity option for
//             maximum Node.js version portability.
// 'recent' — Returns 0 for every pair (stable-order proxy). Documented in
//             D-05/D-07: vendors lack a createdAt field; current list order is
//             the intended proxy until a timestamp is backfilled.
// ---------------------------------------------------------------------------

export function makeVendorSortComparator(sortKey, dealCounts) {
    return (a, b) => {
        if (sortKey === 'deals') {
            return (dealCounts.get(b.id) || 0) - (dealCounts.get(a.id) || 0);
        }
        if (sortKey === 'name') {
            const nameA = displayName(a).toLowerCase();
            const nameB = displayName(b).toLowerCase();
            return nameA.localeCompare(nameB);
        }
        // 'recent' — stable proxy (no createdAt field)
        return 0;
    };
}

// ---------------------------------------------------------------------------
// buildVendorDealCounts — builds a Map<vendorId, number> from a transactions
// array (all-time, per D-03).
//
// Field coverage (confirmed in src/hooks/useTransactionFormState.js lines 62-63
// and 283-294):
//
//   Current array fields:
//     titleCompanyIds  string[]   guarded with || []
//     lenderIds        string[]   guarded with || []
//     buyerAgentIds    string[]   guarded with || []
//     listingAgentIds  string[]   guarded with || []
//
//   Current single field:
//     referralAgentId  string     counted when truthy
//
//   Legacy singular fields (raw Firestore docs not yet migrated by
//   migrateToArray in useTransactionFormState):
//     titleCompanyId   string     counted when truthy
//     lenderId         string     counted when truthy
//     buyerAgentId     string     counted when truthy
//     listingAgentId   string     counted when truthy
//
// Each reference is an independent deal-link, so a vendor referenced via both
// an array field AND a legacy singular field in the same transaction receives
// +1 for each occurrence (two distinct references = two links).
//
// Thread safety / T-27-01 mitigation: array fields are guarded with `|| []`
// and singular fields with an explicit truthiness check so malformed/undefined
// fields never throw.
// ---------------------------------------------------------------------------

function increment(map, id) {
    map.set(id, (map.get(id) || 0) + 1);
}

export function buildVendorDealCounts(transactions) {
    const counts = new Map();

    for (const txn of transactions) {
        // Array fields (current model)
        for (const id of (txn.titleCompanyIds  || [])) increment(counts, id);
        for (const id of (txn.lenderIds        || [])) increment(counts, id);
        for (const id of (txn.buyerAgentIds    || [])) increment(counts, id);
        for (const id of (txn.listingAgentIds  || [])) increment(counts, id);

        // Single field (current model)
        if (txn.referralAgentId) increment(counts, txn.referralAgentId);

        // Legacy singular fields (un-migrated Firestore docs)
        if (txn.titleCompanyId) increment(counts, txn.titleCompanyId);
        if (txn.lenderId)       increment(counts, txn.lenderId);
        if (txn.buyerAgentId)   increment(counts, txn.buyerAgentId);
        if (txn.listingAgentId) increment(counts, txn.listingAgentId);
    }

    return counts;
}

// ---------------------------------------------------------------------------
// computeMostUsedVendor — returns { name, count } for the vendor with the
// highest all-time deal count (D-03, D-04 KPI tile: "Most-used").
//
// Name resolution: company first, then trimmed "First Last" fallback.
//
// Edge cases:
//   - allVendors empty → null (caller should suppress the tile or show a dash).
//   - All counts 0 → returns first vendor with count 0 (caller renders
//     "—" / "0 deals" per the UI-SPEC; count === 0 is explicitly asserted by
//     the test suite to prevent silent promotion of a wrong vendor).
//   - Vendor absent from dealCounts → treated as 0.
// ---------------------------------------------------------------------------

export function computeMostUsedVendor(allVendors, dealCounts) {
    if (!allVendors || allVendors.length === 0) return null;

    let best = null;
    for (const vendor of allVendors) {
        const count = dealCounts.get(vendor.id) || 0;
        if (best === null || count > best.count) {
            best = { name: displayName(vendor), count };
        }
    }
    return best;
}
